import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tradingService, type AlgorithmConfig } from '../../services/trading';
import { BuyRestrictionDialog } from './BuyRestrictionDialog';
import { HoldLimitDialog } from './HoldLimitDialog';

export function AlgorithmConfigTable() {
  const queryClient = useQueryClient();
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [buyRestrictionDialog, setBuyRestrictionDialog] = useState<{
    isOpen: boolean;
    configId: string | null;
    currentMinutes: number;
  }>({
    isOpen: false,
    configId: null,
    currentMinutes: 0,
  });
  const [holdLimitDialog, setHoldLimitDialog] = useState<{
    isOpen: boolean;
    configId: string | null;
    currentMinutes: number;
  }>({
    isOpen: false,
    configId: null,
    currentMinutes: 0,
  });

  // Fetch all algorithm configurations for the user
  const { data: algorithmConfigs = [], isLoading } = useQuery({
    queryKey: ['algorithmConfigs'],
    queryFn: () => tradingService.getAlgorithmConfigs(),
  });

  // Mutation to toggle algorithm on/off
  const toggleMutation = useMutation({
    mutationFn: tradingService.toggleAlgorithmConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
    },
    onError: (error: any) => {
      console.error('Toggle algorithm error:', error);
      alert(error.response?.data?.error || 'Failed to toggle algorithm');
    },
  });

  // Mutation to toggle closeDaily
  const toggleCloseDailyMutation = useMutation({
    mutationFn: tradingService.toggleCloseDaily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
    },
    onError: (error: any) => {
      console.error('Toggle closeDaily error:', error);
      alert(error.response?.data?.error || 'Failed to toggle Close Daily');
    },
  });

  // Mutation to update buy restriction
  const updateBuyRestrictionMutation = useMutation({
    mutationFn: ({ configId, minutes }: { configId: string; minutes: number }) =>
      tradingService.updateBuyRestriction(configId, minutes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      setBuyRestrictionDialog({ isOpen: false, configId: null, currentMinutes: 0 });
    },
    onError: (error: any) => {
      console.error('Update buy restriction error:', error);
      alert(error.response?.data?.error || 'Failed to update buy restriction');
    },
  });

  // Mutation to update hold limit
  const updateHoldLimitMutation = useMutation({
    mutationFn: ({ configId, minutes }: { configId: string; minutes: number }) =>
      tradingService.updateHoldLimit(configId, minutes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      setHoldLimitDialog({ isOpen: false, configId: null, currentMinutes: 0 });
    },
    onError: (error: any) => {
      console.error('Update hold limit error:', error);
      alert(error.response?.data?.error || 'Failed to update hold limit');
    },
  });

  // Mutation to delete algorithm configuration
  const deleteMutation = useMutation({
    mutationFn: tradingService.deleteAlgorithmConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
    },
    onError: (error: any) => {
      console.error('Delete algorithm error:', error);
      alert(error.response?.data?.error || 'Failed to delete algorithm configuration');
    },
  });

  const handleToggle = (configId: string) => {
    toggleMutation.mutate(configId);
  };

  const handleToggleCloseDaily = (configId: string) => {
    toggleCloseDailyMutation.mutate(configId);
  };

  const handleOpenBuyRestrictionDialog = (config: AlgorithmConfig) => {
    setBuyRestrictionDialog({
      isOpen: true,
      configId: config.id,
      currentMinutes: config.buyRestrictionMinutes || 0,
    });
  };

  const handleConfirmBuyRestriction = (minutes: number) => {
    if (buyRestrictionDialog.configId) {
      updateBuyRestrictionMutation.mutate({
        configId: buyRestrictionDialog.configId,
        minutes,
      });
    }
  };

  const handleOpenHoldLimitDialog = (config: AlgorithmConfig) => {
    setHoldLimitDialog({
      isOpen: true,
      configId: config.id,
      currentMinutes: config.holdLimitMinutes || 0,
    });
  };

  const handleConfirmHoldLimit = (minutes: number) => {
    if (holdLimitDialog.configId) {
      updateHoldLimitMutation.mutate({
        configId: holdLimitDialog.configId,
        minutes,
      });
    }
  };

  const handleDelete = (configId: string) => {
    if (confirm('Are you sure you want to delete this algorithm configuration? This cannot be undone.')) {
      deleteMutation.mutate(configId);
    }
  };

  const toggleSecretVisibility = (configId: string) => {
    setShowSecrets(prev => ({ ...prev, [configId]: !prev[configId] }));
  };

  // Helper function to format buy restriction label
  const formatBuyRestrictionLabel = (minutes: number): string => {
    if (minutes === 0) return 'Never';
    if (minutes < 60) return `${minutes}m before`;
    const hours = minutes / 60;
    return `${hours}h before`;
  };

  // Helper function to format hold limit label
  const formatHoldLimitLabel = (minutes: number): string => {
    if (minutes === 0) return 'Never';
    if (minutes < 60) return `${minutes} min`;
    const hours = minutes / 60;
    if (hours % 1 === 0) return `${hours}h`;
    return `${hours.toFixed(1)}h`;
  };

  // Helper function to calculate cutoff time in ET for tooltip
  const formatCutoffTimeET = (minutes: number): string => {
    if (minutes === 0) return '';
    // Market close: 1:00 PM PST = 4:00 PM ET = 16:00 = 960 minutes from midnight
    const marketCloseMinutesET = 16 * 60; // 960
    const cutoffMinutes = marketCloseMinutesET - minutes;
    const hours = Math.floor(cutoffMinutes / 60);
    const mins = cutoffMinutes % 60;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm} ET`;
  };

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const webhookUrl = `${apiUrl}/api/webhooks/tradingview`;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (algorithmConfigs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No webhook configurations found. Use the Webhook Trading Setup above to create your first connection.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
        <div className="font-medium text-blue-800 dark:text-blue-200 mb-2">🔗 Webhook URL for TradingView</div>
        <div className="text-xs text-blue-700 dark:text-blue-300 mb-2">
          Use this URL in your TradingView alert webhook settings:
        </div>
        <code className="block bg-blue-100 dark:bg-blue-900 p-2 rounded text-xs break-all text-blue-900 dark:text-blue-100">
          {webhookUrl}
        </code>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Shares per Trade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Webhook Secret
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Close Daily
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Buy Restriction
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Hold Limit
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
            {algorithmConfigs.map((config: AlgorithmConfig) => (
              <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="inline-flex items-center space-x-2">
                    <span 
                      className={`inline-block w-2 h-2 rounded-full ${
                        config.isActive ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      title={config.isActive ? 'Active' : 'Inactive'}
                    />
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {config.algorithmType}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {config.accountId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {config.units}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-xs">
                    {config.webhookSecret ? (
                      <div className="flex items-center space-x-2">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs break-all text-gray-900 dark:text-gray-100" style={{ maxWidth: '200px' }}>
                          {config.webhookSecret}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(config.webhookSecret || '');
                          }}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">Not available</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleCloseDaily(config.id)}
                    disabled={toggleCloseDailyMutation.isPending}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      config.closeDaily
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    } disabled:opacity-50 cursor-pointer`}
                    title={config.closeDaily ? 'Auto-sell at 12:55 PM PST (Click to disable)' : 'Click to enable auto-sell at 12:55 PM PST'}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      config.closeDaily ? 'bg-white' : 'bg-gray-600'
                    }`}></span>
                    {toggleCloseDailyMutation.isPending ? 'Updating...' : config.closeDaily ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleOpenBuyRestrictionDialog(config)}
                    className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      config.buyRestrictionMinutes === 0
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                    }`}
                    title={config.buyRestrictionMinutes > 0 ? formatCutoffTimeET(config.buyRestrictionMinutes) : 'No buy restriction'}
                  >
                    {formatBuyRestrictionLabel(config.buyRestrictionMinutes)}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleOpenHoldLimitDialog(config)}
                    className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      config.holdLimitMinutes === 0
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                    }`}
                    title={config.holdLimitMinutes > 0 ? `Auto-sell after ${formatHoldLimitLabel(config.holdLimitMinutes)}` : 'No hold limit'}
                  >
                    {formatHoldLimitLabel(config.holdLimitMinutes)}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleDelete(config.id)}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800 transition-colors disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : '🗑️ Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Buy Restriction Dialog */}
      <BuyRestrictionDialog
        isOpen={buyRestrictionDialog.isOpen}
        onClose={() => setBuyRestrictionDialog({ isOpen: false, configId: null, currentMinutes: 0 })}
        onConfirm={handleConfirmBuyRestriction}
        currentMinutes={buyRestrictionDialog.currentMinutes}
        isLoading={updateBuyRestrictionMutation.isPending}
      />

      {/* Hold Limit Dialog */}
      <HoldLimitDialog
        isOpen={holdLimitDialog.isOpen}
        onClose={() => setHoldLimitDialog({ isOpen: false, configId: null, currentMinutes: 0 })}
        onConfirm={handleConfirmHoldLimit}
        currentMinutes={holdLimitDialog.currentMinutes}
        isLoading={updateHoldLimitMutation.isPending}
      />
    </div>
  );
}